import React from "react";
import styled from "styled-components";
import { expandIcon } from "assets/icons";
import Icon from "components/Icon";
import { pxToRem } from "styles/utils";

export type StatisticCardVariant = "small" | "big";

interface StatisticCardProps {
  id: string;
  title: string;
  value: string;
  delta: number | null;
  variant: StatisticCardVariant;
  onActivate?: () => void;
  children: React.ReactNode;
  deltaLabel?: string;
}

const CardContainer = styled.div<{ $variant: StatisticCardVariant }>`
  display: flex;
  align-self: stretch;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  grid-column: ${({ $variant }) => ($variant === "big" ? "1 / -1" : "auto")};
  transform-origin: top left;
  will-change: transform;

  @media (max-width: 1000px) {
    height: auto;
  }
`;

const Card = styled.article<{ $interactive: boolean }>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: ${pxToRem(20)} ${pxToRem(22.5)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(17.5)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  box-sizing: border-box;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ $interactive }) =>
      $interactive ? "rgba(114, 82, 220, 0.4)" : "rgba(31, 10, 53, 0.14)"};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  @media (max-width: 1000px) {
    height: auto;
  }
`;

const CardHead = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${pxToRem(15)};
`;

const CardTitle = styled.div`
  min-width: 0;
  font-size: ${pxToRem(18.75)};
  font-weight: 500;
`;

const ExpandHint = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  margin: ${pxToRem(-5)} ${pxToRem(-5)} 0 0;
  padding: ${pxToRem(5)};
  border-radius: ${pxToRem(7.5)};
  color: ${({ theme }) => theme.colors.inkMuted};
  transition: color 0.15s ease, background-color 0.15s ease;

  ${Card}:hover & {
    color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSoft};
  }
`;

const CardValue = styled.div<{ $variant: StatisticCardVariant }>`
  flex: 0 0 auto;
  margin: ${pxToRem(10)} 0 ${pxToRem(2.5)};
  font-size: ${({ $variant }) => pxToRem($variant === "big" ? 37.5 : 27.5)};
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
`;

const Delta = styled.div<{ $tone: "up" | "down" | "neutral" }>`
  flex: 0 0 auto;
  min-height: ${pxToRem(20)};
  color: ${({ $tone, theme }) =>
    $tone === "up"
      ? "#4b9e6a"
      : $tone === "down"
        ? "#b0304c"
        : theme.colors.inkMuted};
  font-size: ${pxToRem(16.25)};
  font-variant-numeric: tabular-nums;
`;

const DeltaLabel = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
`;

const CardBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  height: auto;
  margin-top: ${pxToRem(12.5)};
  overflow: hidden;

  @media (max-width: 1000px) {
    flex: 0 0 ${pxToRem(220)};
    height: ${pxToRem(220)};
  }
`;

const StatisticCard = React.forwardRef<HTMLDivElement, StatisticCardProps>(({
  id,
  title,
  value,
  delta,
  variant,
  onActivate,
  children,
  deltaLabel = "к прошлому периоду",
}, ref) => {
  const interactive = variant === "small" && Boolean(onActivate);
  const tone = delta === null ? "neutral" : delta >= 0 ? "up" : "down";
  const deltaText = delta === null ? null : `${delta > 0 ? "+" : ""}${delta}%`;

  const activate = () => {
    if (interactive) {
      onActivate?.();
    }
  };

  return (
    <CardContainer
      ref={ref}
      $variant={variant}
      data-statistic-card-id={id}
      data-statistic-card-variant={variant}
    >
      <Card
        $interactive={interactive}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? `Развернуть панель «${title}»` : undefined}
        onClick={interactive ? activate : undefined}
        onKeyDown={
          interactive
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  activate();
                }
              }
            : undefined
        }
      >
        <CardHead>
          <CardTitle>{title}</CardTitle>
          {interactive && (
            <ExpandHint aria-hidden="true">
              <Icon glyph={expandIcon} size="md" />
            </ExpandHint>
          )}
        </CardHead>
        <CardValue $variant={variant}>{value}</CardValue>
        <Delta $tone={tone}>
          {deltaText ? (
            <>{deltaText} <DeltaLabel>{deltaLabel}</DeltaLabel></>
          ) : (
            <DeltaLabel>нет данных за прошлый период</DeltaLabel>
          )}
        </Delta>
        <CardBody>{children}</CardBody>
      </Card>
    </CardContainer>
  );
});

StatisticCard.displayName = "StatisticCard";

export default StatisticCard;
